import {
  adapterAreaToAreaSource,
  floorPlanModelFromPublicAreaSource,
  partialUppFloorPlanPreferencesFromPublicAreaSource,
  uppKonvaAreaWire,
  type UppKonvaAreaSpatialMode,
} from '../plan/adapters/floor-plan-from-area-source';
import type { UppKonvaAreaSource } from '../../model/upp-konva-source';
import type { UppKonvaAttention } from '../../model/upp-konva-types';
import {
  serializeFloorPlanDocument,
  UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION,
  uppFloorPlanAreaSourceFromModel,
  parseFloorPlanDocument,
  type SerializeFloorPlanDocumentOptions,
  type UppFloorPlanParseDocumentResult,
} from '../plan/serialization/floor-plan-document';
import {
  UppFloorPlanArchitecture,
  UppFloorPlanBar,
  UppFloorPlanFurniture,
  UppFloorPlanTable,
  UppFloorPlanVertex,
  type UppFloorPlanBarShape,
  type UppFloorPlanElement,
  type UppFloorPlanGenericShape,
  type UppFloorPlanSofaShape,
  type UppFloorPlanTableShape,
} from '../plan/floor-plan-element';
import { UppFloorPlanModel, type UppKonvaRoom } from '../plan/floor-plan-model';
import {
  mergeUppFloorPlanPreferences,
  type UppFloorPlanPreferences,
} from '../plan/floor-plan-preferences';
import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';
import type { UppKonvaRefresh } from '../../model/upp-konva-refresh';
import { createUppKonvaShapeHandleForElement, type UppKonvaShapeHandle } from '../presentation/upp-konva-shape-handle';
import type { UppFloorPlanExportPngOptions } from '../export/upp-floor-plan-export-png';
import type { UppKonvaFloorPlanGroup } from '../presentation/upp-konva-floor-plan-group';
import { roundFloorPlanGeometryCm } from '../geometry/floor-plan-konva-sync';
import { floorPlanElementUuid, newFloorPlanUuidV4 } from './floor-plan-uuid';
import { uppKonvaRuntimeTrace } from '../../internal/upp-konva-runtime-trace';
import { registerUppKonvaSceneModelHost, type UppKonvaSceneModelHost } from './upp-konva-scene-model-host';

function _presentationOptsFromElement(el: UppFloorPlanElement): {
  label: string;
  sublabel: string;
  attention: boolean;
  attentionTone: UppKonvaAttention;
  attentionAnimated: boolean;
  custom: unknown;
} {
  return {
    label: el.label,
    sublabel: el.sublabel,
    attention: el.attention,
    attentionTone: el.attentionTone,
    attentionAnimated: el.attentionAnimated,
    custom: el.custom,
  };
}

function _normalizePublicBarShape(raw: unknown): UppFloorPlanBarShape {
  const u = typeof raw === 'string' ? raw.toUpperCase() : '';
  if (u === 'LINEAR' || u === 'TOLEFT' || u === 'TORIGHT' || u === 'USHAPE') {
    return u;
  }
  return 'LINEAR';
}

function _normalizePublicSofaShape(raw: unknown): UppFloorPlanSofaShape {
  const u = typeof raw === 'string' ? raw.toUpperCase() : '';
  if (u === 'TOLEFT' || u === 'TORIGHT') {
    return u;
  }
  return 'LINEAR';
}

function _genericShapeFromPublicData(raw: unknown): UppFloorPlanGenericShape {
  const u = typeof raw === 'string' ? raw.toUpperCase() : '';
  if (u === 'ROUND' || u === 'CIRCLE' || u === 'ELLIPSE') {
    return 'GENRND';
  }
  return 'GENRCT';
}

function _architectureShapeFromPublicData(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string' || raw.length === 0) {
    return fallback;
  }
  return raw.toUpperCase();
}

function _optionalTruncStool(raw: unknown, fallback: number | undefined): number | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return fallback;
  }
  return Math.trunc(raw);
}

/**
 * Presentation-side scene: mutable element list; on **structural** updates, the internal aggregate is recreated
 * so Konva host detects list changes. Geometry-only edits keep the same aggregate; host refreshes canvas
 * (`refreshFromModel` is wired inside **`upp-konva`**).
 *
 * Construct with **`new UppKonvaFloorPlanModel(areaSource, preferences?)`** from {@link UppKonvaAreaSource}
 * (public DTO `kind`; optional in-memory **`onGrouped`**).
 *
 * Surface aligned with plan contract: **`area`**, **`mode`**, **`theme`**, **`shapes`**, **`onModelChanged`**,
 * **`importModel`**, **`exportModel`**, **`exportToPng`**. Stage wiring uses **`uppKonvaSceneModelHost`**
 * (internal package module). **`uppKonvaRefresh`** / **`doRefresh`** aligns public host with stub
 * **`UppKonvaModel`** contract (`model/upp-konva-model.ts`).
 */
export class UppKonvaFloorPlanModel {
  private static readonly _PERIMETER_VERTEX_PLACE_OFFSET_CM = 20;

  private static _presentationPreferencesEqual(
    a: UppFloorPlanPreferences,
    b: UppFloorPlanPreferences,
  ): boolean {
    return (
      a.marginPx === b.marginPx &&
      a.autoOptimizeOrientation === b.autoOptimizeOrientation &&
      a.viewOrientationDeg === b.viewOrientationDeg &&
      a.pixelRatioMax === b.pixelRatioMax &&
      a.viewZoom === b.viewZoom &&
      a.labelSize === b.labelSize
    );
  }

  private readonly _room: UppKonvaRoom;
  private readonly _elements: UppFloorPlanElement[] = [];
  private _sceneModel: UppFloorPlanModel;
  private _preferences: UppFloorPlanPreferences;
  private readonly _itemByElement = new WeakMap<UppFloorPlanElement, UppKonvaShapeHandle>();
  private _refreshListener: (() => void) | null = null;
  private _floorRefresh: (() => void) | null = null;
  private _exportToPng: ((options?: UppFloorPlanExportPngOptions) => Promise<Blob | null>) | null = null;
  /**
   * Invoked when the persistable **`shapes`** set changes (list replacement or equivalent): see **`OnModelChanged`** plan.
   * It does not fire for only **`model.mode`**, theme, or preferences; those changes repaint through internal
   * **`onRefresh`** registration when host **`upp-konva`** is mounted.
   */
  onModelChanged: (() => void) | null = null;

  private _onGroupedCb: ((group: UppKonvaFloorPlanGroup | null) => void) | null = null;
  private _areaSpatialMode: UppKonvaAreaSpatialMode = 'SHAPES';

  private _editMode = false;
  private _perimeterDraftPolygonId: string | null = null;
  private _vertexPreviewAnchorId: string | null = null;
  private _theme: Partial<UppFloorPlanTheme> | null = null;

  /**
   * Incremented each time the internal {@link UppFloorPlanModel} (`_sceneModel`) is replaced.
   * The host binds this value to the stage to force repaint with OnPush: the reference to {@link UppKonvaFloorPlanModel}
   * that the host receives from `upp-konva` is stable even when the scene snapshot is new.
   */
  private _presentationEpoch = 0;

  /** Integrator registration (contract {@link UppKonvaRefresh} in `model/`); the host `upp-konva` wraps it. */
  private _uppKonvaRefresh: UppKonvaRefresh | null = null;

  get presentationEpoch(): number {
    return this._presentationEpoch;
  }

  get uppKonvaRefresh(): UppKonvaRefresh | null {
    return this._uppKonvaRefresh;
  }

  set uppKonvaRefresh(value: UppKonvaRefresh | null) {
    this._uppKonvaRefresh = value;
  }

  constructor(area: UppKonvaAreaSource & { onGrouped?: (group: UppKonvaFloorPlanGroup | null) => void }, preferences?: Partial<UppFloorPlanPreferences> | null) {
    const extended = area;
    this._onGroupedCb = extended.onGrouped ?? null;
    const { onGrouped: _og, ...dto } = extended;
    void _og;
    const base = floorPlanModelFromPublicAreaSource(dto);
    this._areaSpatialMode = dto.mode === 'VERTEX' ? 'VERTEX' : 'SHAPES';
    this._room = base.room;
    this._elements.push(...base.elements);
    const hostPrefs = mergeUppFloorPlanPreferences(preferences ?? null);
    this._preferences = mergeUppFloorPlanPreferences({
      ...hostPrefs,
      ...partialUppFloorPlanPreferencesFromPublicAreaSource(dto),
    });
    this._sceneModel = new UppFloorPlanModel(this._room, this._elements);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const modelHost: UppKonvaSceneModelHost = {
      get sceneModel() {
        return self._sceneModel;
      },
      get presentationPreferences() {
        return self._preferences;
      },
      setPresentationPreferences(partial: Partial<UppFloorPlanPreferences> | null | undefined): void {
        self.setPresentationPreferences(partial);
      },
      onRefresh(fn: (() => void) | null): void {
        self._refreshListener = fn;
      },
      bindFloorRefresh(fn: (() => void) | null): void {
        self._floorRefresh = fn;
      },
      bindExportToPng(
        fn: ((options?: UppFloorPlanExportPngOptions) => Promise<Blob | null>) | null,
      ): void {
        self._exportToPng = fn;
      },
      notifyGrouped(group: UppKonvaFloorPlanGroup | null): void {
        self._onGroupedCb?.(group);
      },
      addItem(element: UppFloorPlanElement): UppKonvaShapeHandle {
        return self._addItem(element);
      },
      delItem(item: UppKonvaShapeHandle): boolean {
        return self._delItem(item);
      },
      removeElementByUuid(uuid: string): boolean {
        return self._removeElementByUuid(uuid);
      },
      setShapesFromElements(elements: readonly UppFloorPlanElement[]): void {
        self._setShapesFromElements(elements);
      },
      get modelVersion() {
        return UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION;
      },
      get vertexPreviewAnchorId() {
        return self._vertexPreviewAnchorId;
      },
      onRoomBackgroundDoubleClickCm(xCm: number, yCm: number): string | null {
        return self._onRoomBackgroundDoubleClickCm(xCm, yCm);
      },
      onRoomBackgroundSingleClick(): void {
        self._onRoomBackgroundSingleClick();
      },
      onVertexDoubleClick(vertexUuid: string): void {
        self._onVertexDoubleClick(vertexUuid);
      },
      addVertexAtRoomCentre(): string | null {
        return self._addVertexAtRoomCentre();
      },
    };
    registerUppKonvaSceneModelHost(this, modelHost);
  }

  /**
   * Chains an observer after DTO optional `onGrouped` (e.g. bridge to {@link UppKonvaArea.onGrouped$}).
   * It does not replace the existing callback: it runs **after** the previous one.
   */
  chainOnGrouped(next: (group: UppKonvaFloorPlanGroup | null) => void): void {
    const prev = this._onGroupedCb;
    this._onGroupedCb = (g) => {
      prev?.(g);
      next(g);
    };
  }

  /**
   * Presentation repaint: internal engine + integrator host callback (without structural **`onModelChanged`**).
   */
  doRefresh(): void {
    this._refreshListener?.();
    this._uppKonvaRefresh?.doRefresh();
  }

  /**
   * Clears integrator registration; host must restore **`onModelChanged`** / **`uppKonvaRefresh`** beforehand.
   * **`uppKonvaSceneModelHost()`** still resolves to allow atomic mutation (e.g. preferences) without mounted stage.
   */
  doDestroy(): void {
    this._uppKonvaRefresh = null;
  }

  /**
   * **`VIEW`**: constrained interaction; **`EDIT`**: Konva editing (selection, transformer, perimeter when
   * **`area.source.mode`** / snapshot is **`VERTEX`**).
   */
  get mode(): 'VIEW' | 'EDIT' {
    return this._editMode ? 'EDIT' : 'VIEW';
  }

  set mode(value: 'VIEW' | 'EDIT') {
    const next = value === 'EDIT';
    if (this._editMode === next) {
      return;
    }
    this._editMode = next;
    if (!next) {
      this._perimeterDraftPolygonId = null;
      this._vertexPreviewAnchorId = null;
    }
    this._refreshListener?.();
  }

  /**
   * Read/write partial {@link UppFloorPlanTheme} forwarded to the inner stage under **`upp-konva`** (merged over the host default light base).
   */
  get theme(): Partial<UppFloorPlanTheme> | null {
    return this._theme;
  }

  set theme(value: Partial<UppFloorPlanTheme> | null | undefined) {
    const next = value === undefined ? null : value;
    if (this._theme === next) {
      return;
    }
    this._theme = next;
    this._refreshListener?.();
  }

  /**
   * Merges framing preferences (margin, zoom, orientation, …) onto the live canvas and requests a repaint.
   */
  setPresentationPreferences(partial: Partial<UppFloorPlanPreferences> | null | undefined): void {
    const next = mergeUppFloorPlanPreferences({
      ...this._preferences,
      ...partial,
    });
    if (UppKonvaFloorPlanModel._presentationPreferencesEqual(next, this._preferences)) {
      return;
    }
    this._preferences = next;
    this._refreshListener?.();
  }

  private _onRoomBackgroundDoubleClickCm(xCm: number, yCm: number): string | null {
    if (!this._editMode || this._areaSpatialMode !== 'VERTEX') {
      return null;
    }
    if (this._perimeterDraftPolygonId === null) {
      this._perimeterDraftPolygonId = this._allocPolygonId();
    }
    const pid = this._perimeterDraftPolygonId;
    const inDraft = this._elements.filter(
      (e): e is UppFloorPlanVertex =>
        e instanceof UppFloorPlanVertex && e.polygon === pid && !e.closed,
    );
    const nextSort = inDraft.length;
    const id = newFloorPlanUuidV4();
    const half = UppKonvaFloorPlanModel._PERIMETER_VERTEX_PLACE_OFFSET_CM;
    const vx = new UppFloorPlanVertex(
      id,
      roundFloorPlanGeometryCm(xCm - half),
      roundFloorPlanGeometryCm(yCm - half),
      nextSort,
      40,
      40,
      0,
      undefined,
      pid,
      false,
    );
    this._elements.push(vx);
    this._vertexPreviewAnchorId = id;
    this._refreshListener?.();
    this._bumpSceneModel();
    this._fireModelChanged();
    return id;
  }

  private _onRoomBackgroundSingleClick(): void {
    if (!this._editMode || this._areaSpatialMode !== 'VERTEX' || this._perimeterDraftPolygonId === null) {
      return;
    }
    const pid = this._perimeterDraftPolygonId;
    const draft = this._elements.filter(
      (e): e is UppFloorPlanVertex =>
        e instanceof UppFloorPlanVertex && e.polygon === pid && !e.closed,
    );
    if (draft.length >= 3) {
      for (const e of this._elements) {
        if (e instanceof UppFloorPlanVertex && e.polygon === pid && !e.closed) {
          e.closed = true;
        }
      }
      /** Sync `data.closed` in public DTO (`applyAdapterSnapshotFromStage`); otherwise `area.source` becomes stale and a later `applyAreaFromSource` reopens the polygon. */
      this._fireModelChanged();
    } else {
      const removeIds = new Set(draft.map((e) => e.uuid));
      for (let i = this._elements.length - 1; i >= 0; i -= 1) {
        const row = this._elements[i];
        if (!row) {
          continue;
        }
        if (removeIds.has(row.uuid)) {
          this._elements.splice(i, 1);
        }
      }
      this._bumpSceneModel();
      this._fireModelChanged();
    }
    this._perimeterDraftPolygonId = null;
    this._vertexPreviewAnchorId = null;
    this._refreshListener?.();
    if (draft.length >= 3) {
      this._notifyRefresh();
    }
  }

  private _onVertexDoubleClick(vertexUuid: string): void {
    if (!this._editMode || this._areaSpatialMode !== 'VERTEX') {
      return;
    }
    const row = this._elements.find((e) => e.uuid === vertexUuid);
    if (!(row instanceof UppFloorPlanVertex)) {
      return;
    }
    if (row.closed) {
      for (const e of this._elements) {
        if (e instanceof UppFloorPlanVertex && e.polygon === row.polygon) {
          e.closed = false;
        }
      }
      this._fireModelChanged();
    }
    this._perimeterDraftPolygonId = row.polygon;
    this._vertexPreviewAnchorId = row.uuid;
    this._refreshListener?.();
    this._notifyRefresh();
  }

  private _addVertexAtRoomCentre(): string | null {
    const cx = this._room.widthCm / 2;
    const cy = this._room.heightCm / 2;
    return this._onRoomBackgroundDoubleClickCm(cx, cy);
  }

  /** PNG (or other raster, based on options) from current canvas; `null` when host is not bound or not ready. */
  exportToPng(options?: UppFloorPlanExportPngOptions): Promise<Blob | null> {
    return this._exportToPng ? this._exportToPng(options) : Promise.resolve(null);
  }

  /**
   * Read: public **DTO snapshot** (`kind` + `data`) aligned with `model.area.source`, plus **`onGrouped`** if wired.
   * Write: replaces area and syncs the canvas.
   */
  get area(): UppKonvaAreaSource & { onGrouped?: (group: UppKonvaFloorPlanGroup | null) => void } {
    const wire = uppFloorPlanAreaSourceFromModel(this._sceneModel, this._preferences, this._areaSpatialMode);
    const base = adapterAreaToAreaSource(wire);
    return this._onGroupedCb ? { ...base, onGrouped: this._onGroupedCb } : base;
  }

  set area(value: UppKonvaAreaSource & { onGrouped?: (group: UppKonvaFloorPlanGroup | null) => void }) {
    const extended = value;
    this._onGroupedCb = extended.onGrouped ?? null;
    const { onGrouped: _drop, ...dto } = extended;
    void _drop;
    this._applyLiveAreaFromSource(dto);
  }

  private _fireModelChanged(): void {
    this.onModelChanged?.();
  }

  private _notifyRefresh(): void {
    this._floorRefresh?.();
  }

  /**
   * Iterable of {@link UppKonvaShapeHandle} (stable reference per element). `for…of`, `Array.from`, `[...model.shapes]`.
   */
  get shapes(): Iterable<UppKonvaShapeHandle> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return {
      [Symbol.iterator](): Iterator<UppKonvaShapeHandle> {
        let i = 0;
        const els = self._elements;
        return {
          next(): IteratorResult<UppKonvaShapeHandle> {
            if (i >= els.length) {
              return { done: true, value: undefined };
            }
            const row = els[i];
            if (!row) {
              return { done: true, value: undefined };
            }
            const value = self._wrap(row);
            i += 1;
            return { done: false, value };
          },
        };
      },
    };
  }

  private _setShapesFromElements(elements: readonly UppFloorPlanElement[]): void {
    this._elements.length = 0;
    this._elements.push(...elements);
    this._bumpSceneModel();
  }

  private _addItem(element: UppFloorPlanElement): UppKonvaShapeHandle {
    this._elements.push(element);
    this._bumpSceneModel();
    return this._wrap(element);
  }

  private _delItem(item: UppKonvaShapeHandle): boolean {
    const idx = this._elements.findIndex((el) => this._wrap(el) === item);
    if (idx < 0) {
      return false;
    }
    const row = this._elements[idx];
    if (!row) {
      return false;
    }
    return this._removeElementByUuid(row.uuid);
  }

  private _removeElementByUuid(uuid: string): boolean {
    const t = uuid.trim();
    const needle =
      this._elements.find((e) => e.uuid === t)?.uuid ??
      this._elements.find((e) => e.uuid === floorPlanElementUuid(t))?.uuid;
    if (!needle) {
      return false;
    }
    const i = this._elements.findIndex((e) => e.uuid === needle);
    if (i < 0) {
      return false;
    }
    this._elements.splice(i, 1);
    this._bumpSceneModel();
    return true;
  }

  /**
   * JSON with current **`schemaVersion`** from the current {@link area} (same contract as {@link serializeFloorPlanDocument}).
   */
  exportModel(options?: SerializeFloorPlanDocumentOptions): string {
    const wire = uppFloorPlanAreaSourceFromModel(this._sceneModel, this._preferences, this._areaSpatialMode);
    return serializeFloorPlanDocument(uppKonvaAreaWire(wire), options);
  }

  /**
   * Validated parsing (same rules as {@link parseFloorPlanDocument}); if **`ok`**, applies the parsed **`UppKonvaArea`** to the model (room, scene and derived preferences).
   */
  importModel(json: string): UppFloorPlanParseDocumentResult {
    const r = parseFloorPlanDocument(json);
    if (!r.ok) {
      return r;
    }
    const preserveGrouped = this._onGroupedCb;
    this._applyLiveAreaFromSource(adapterAreaToAreaSource(r.area));
    this._onGroupedCb = preserveGrouped;
    return r;
  }

  private _applyLiveAreaFromSource(source: UppKonvaAreaSource): void {
    const prevSpatial = this._areaSpatialMode;
    const prevShapesJson = JSON.stringify(
      adapterAreaToAreaSource(
        uppFloorPlanAreaSourceFromModel(this._sceneModel, this._preferences, prevSpatial),
      ).shapes,
    );
    const nextShapesJson = JSON.stringify(source.shapes);
    const shapesStructuralChange = prevShapesJson !== nextShapesJson;

    this._areaSpatialMode = source.mode === 'VERTEX' ? 'VERTEX' : 'SHAPES';
    const base = floorPlanModelFromPublicAreaSource(source);
    this._room.widthCm = base.room.widthCm;
    this._room.heightCm = base.room.heightCm;
    if (base.room.name !== undefined) {
      this._room.name = base.room.name;
    } else {
      delete this._room.name;
    }
    this._elements.length = 0;
    this._elements.push(...base.elements);
    this._preferences = mergeUppFloorPlanPreferences(partialUppFloorPlanPreferencesFromPublicAreaSource(source));
    this._sceneModel = new UppFloorPlanModel(this._room, this._elements);
    this._presentationEpoch += 1;
    this._perimeterDraftPolygonId = null;
    this._vertexPreviewAnchorId = null;
    uppKonvaRuntimeTrace('UppKonvaFloorPlanModel._applyLiveAreaFromSource', {
      shapesStructuralChange,
      presentationEpoch: this._presentationEpoch,
      willFloorRefresh: typeof this._floorRefresh === 'function',
      tableShapes: source.shapes
        .filter((r) => r.kind === 'TABLE')
        .map((r) => ({
          shape: typeof r.data === 'object' && r.data !== null && !Array.isArray(r.data)
            ? (r.data as Record<string, unknown>)['shape']
            : undefined,
        })),
    });
    this._refreshListener?.();
    /** Imperative repaint: the `@Input() model` of the stage may still point to the previous snapshot until the next CD (OnPush). */
    this._floorRefresh?.();
    if (shapesStructuralChange) {
      this._fireModelChanged();
    }
  }

  private _bumpSceneModel(): void {
    this._sceneModel = new UppFloorPlanModel(this._room, this._elements);
    this._presentationEpoch += 1;
    this._refreshListener?.();
    this._floorRefresh?.();
  }

  /**
   * Public labels -> live canvas row and repaint ({@link _bumpSceneModel}).
   * Preferred over only {@link UppKonvaArea.notifyPresentationDirty} for text, because badge repaint requires **`_floorRefresh`**.
   */
  applyLabelsFromPublicShape(id: string, label: string, sublabel: string): void {
    const needle = id.trim();
    const idx = this._elements.findIndex(
      (e) => e.uuid === needle || e.uuid === floorPlanElementUuid(needle),
    );
    if (idx < 0) {
      return;
    }
    const el = this._elements[idx];
    if (!el) {
      return;
    }
    const nextLabel = label ?? '';
    const nextSublabel = sublabel ?? '';
    if (el.label === nextLabel && el.sublabel === nextSublabel) {
      return;
    }
    el.label = nextLabel;
    el.sublabel = nextSublabel;
    this._bumpSceneModel();
  }

  /**
   * Aligns visual variant and `data` payload with public shape; **geometry** is always taken from internal element
   * (canonical on canvas) to avoid overriding position/size when public DTO is stale after dragging.
   * To edit x/y/width/height from inspector, flush area with {@link UppKonvaArea.notifyPresentationDirty} + repaint.
   */
  applyPresentationFromPublicShape(shape: { id: string; kind: string; data: Record<string, unknown> }): void {
    const needle = shape.id.trim();
    const idx = this._elements.findIndex(
      (e) => e.uuid === needle || e.uuid === floorPlanElementUuid(needle),
    );
    if (idx < 0) {
      return;
    }
    const el = this._elements[idx];
    if (!el) {
      return;
    }
    const sd = shape.data;
    const x = el.xCm;
    const y = el.yCm;
    const w = el.widthCm;
    const h = el.heightCm;
    const rot = el.rotationDeg;
    const opt = _presentationOptsFromElement(el);

    switch (shape.kind) {
      case 'TABLE': {
        if (!(el instanceof UppFloorPlanTable)) {
          return;
        }
        const raw = sd['shape'];
        const u = typeof raw === 'string' ? raw.toUpperCase() : '';
        const nextShape: UppFloorPlanTableShape = u === 'CIRCLE' ? 'ELLIPSE' : 'RECT';
        const chairsRaw = sd['chairs'];
        const chairCount =
          typeof chairsRaw === 'number' && Number.isFinite(chairsRaw)
            ? Math.trunc(chairsRaw as number)
            : el.chairCount;
        let nextDiners = el.diners;
        if (Object.prototype.hasOwnProperty.call(sd, 'diners')) {
          const dr = sd['diners'];
          nextDiners =
            typeof dr === 'number' && Number.isFinite(dr) ? Math.max(0, Math.trunc(dr as number)) : 0;
        }
        if (nextShape === el.tableShape && chairCount === el.chairCount) {
          if (nextDiners !== el.diners) {
            el.diners = nextDiners;
            this._bumpSceneModel();
          }
          return;
        }
        const next = new UppFloorPlanTable(el.uuid, x, y, w, h, rot, chairCount, nextShape, opt);
        next.diners = nextDiners;
        this._splicePresentationElement(idx, el, next);
        return;
      }
      case 'BAR': {
        if (!(el instanceof UppFloorPlanBar)) {
          return;
        }
        const nextBarShape = _normalizePublicBarShape(sd['shape']);
        const stoolLeft = _optionalTruncStool(sd['stoolLeft'], el.stoolLeft);
        const stoolFront = _optionalTruncStool(sd['stoolFront'], el.stoolFront);
        const stoolRight = _optionalTruncStool(sd['stoolRight'], el.stoolRight);
        if (
          nextBarShape === el.barShape &&
          stoolLeft === el.stoolLeft &&
          stoolFront === el.stoolFront &&
          stoolRight === el.stoolRight
        ) {
          return;
        }
        const next = new UppFloorPlanBar(
          el.uuid,
          x,
          y,
          w,
          h,
          rot,
          nextBarShape,
          stoolLeft,
          stoolFront,
          stoolRight,
          opt,
        );
        this._splicePresentationElement(idx, el, next);
        return;
      }
      case 'DOOR':
      case 'WINDOW':
      case 'STAIRS': {
        if (!(el instanceof UppFloorPlanArchitecture)) {
          return;
        }
        const wantClass =
          shape.kind === 'DOOR' ? 'door' : shape.kind === 'WINDOW' ? 'window' : 'stairs';
        if (el.architectureClass !== wantClass) {
          return;
        }
        const nextArchShape = _architectureShapeFromPublicData(sd['shape'], el.architectureShape);
        if (nextArchShape === el.architectureShape) {
          return;
        }
        const next = new UppFloorPlanArchitecture(
          el.uuid,
          x,
          y,
          w,
          h,
          rot,
          el.architectureClass,
          nextArchShape,
          opt,
        );
        this._splicePresentationElement(idx, el, next);
        return;
      }
      case 'SOFA': {
        if (!(el instanceof UppFloorPlanFurniture) || el.furnitureClass !== 'sofa') {
          return;
        }
        const nextSofa = _normalizePublicSofaShape(sd['shape']);
        if (nextSofa === el.sofaShape) {
          return;
        }
        const next = new UppFloorPlanFurniture(el.uuid, x, y, w, h, rot, 'sofa', undefined, opt, nextSofa);
        this._splicePresentationElement(idx, el, next);
        return;
      }
      case 'GENERIC': {
        if (!(el instanceof UppFloorPlanFurniture) || el.furnitureClass !== 'generic') {
          return;
        }
        const nextGen = _genericShapeFromPublicData(sd['shape']);
        if (nextGen === el.genericShape) {
          return;
        }
        const next = new UppFloorPlanFurniture(el.uuid, x, y, w, h, rot, 'generic', nextGen, opt);
        this._splicePresentationElement(idx, el, next);
        return;
      }
      default:
        return;
    }
  }

  private _splicePresentationElement(idx: number, oldEl: UppFloorPlanElement, next: UppFloorPlanElement): void {
    next.zIndex = oldEl.zIndex;
    next.selected = oldEl.selected;
    this._elements[idx] = next;
    this._itemByElement.delete(oldEl);
    this._bumpSceneModel();
  }

  private _wrap(el: UppFloorPlanElement): UppKonvaShapeHandle {
    let item = this._itemByElement.get(el);
    if (!item) {
      item = createUppKonvaShapeHandleForElement(el, () => this._notifyRefresh());
      this._itemByElement.set(el, item);
    }
    return item;
  }

  private _allocPolygonId(): string {
    return newFloorPlanUuidV4();
  }
}
